<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronRight, MessageSquare, Folder, Link2, Plus } from 'lucide-svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import { tasksApi } from '$lib/api/tasks';
  import { membersApi } from '$lib/api/members';
  import { adminUsersApi } from '$lib/api/admin';
  import { deploysApi } from '$lib/api/deploys';
  import { projectsApi } from '$lib/api/projects';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { relTime, pretty, ticketKind, ticketAgeDays, siteCode } from '$lib/utils/format';
  import { TASK_COLUMNS } from '$lib/types';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import SrcBadge from './SrcBadge.svelte';
  import SlaChip from './SlaChip.svelte';

  const { ticketId }: { ticketId: number } = $props();
  const queryClient = useQueryClient();
  const admin = $derived(session.isAdmin);

  const ticketQuery = createQuery(() => ({
    queryKey: ['ticket', ticketId],
    queryFn: () => ticketsApi.get(ticketId)
  }));
  const commentsQuery = createQuery(() => ({
    queryKey: ['ticket-comments', ticketId],
    queryFn: () => ticketsApi.comments(ticketId)
  }));
  const ticket = $derived(ticketQuery.data);

  // Name lookup for comment authors / assignee. Members endpoint covers
  // non-admin project grantees (the one user-listing surface a non-admin
  // can reach); admins aren't in that list at all (they're not "granted" —
  // they bypass scoping entirely), so an admin author/assignee needs the
  // admin users list too — fetched whenever the viewer is admin, not just
  // for project-less inbox tickets, since an admin can comment on/be
  // assigned any ticket. Also seed with the viewer's own name so it never
  // depends on a round-trip finishing first.
  const namesQuery = createQuery(() => ({
    queryKey: ['ticket-names', ticket?.project_id, admin],
    queryFn: async () => {
      const map = new Map<number, string>();
      if (session.me) map.set(session.me.user.id, session.me.user.name || session.me.user.email);
      if (ticket?.project_id) {
        const r = await membersApi.forProject(ticket.project_id);
        r.results.forEach((u) => map.set(u.id, u.name));
      }
      if (admin) {
        const r = await adminUsersApi.list({ per_page: 100 });
        r.results.forEach((u) => map.set(u.id, u.name || u.email));
      }
      return map;
    },
    enabled: !!ticket
  }));
  function userName(id: number | null): string {
    if (id === null) return 'Unassigned';
    return namesQuery.data?.get(id) ?? `User #${id}`;
  }

  const deploysQuery = createQuery(() => ({
    queryKey: ['deploys'],
    queryFn: () => deploysApi.list(),
    enabled: !!ticket?.project_id
  }));
  const projectDeploys = $derived(
    (deploysQuery.data?.results ?? [])
      .filter((d) => d.project_id === ticket?.project_id)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  );

  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));
  function projectName(id: number): string {
    return projectsQuery.data?.results.find((p) => p.id === id)?.name ?? `Project #${id}`;
  }

  // Same cache key the board tab uses (['project-tasks', id]) — reuses
  // whatever's already fetched instead of a dedicated "linked tasks" call,
  // since linkage is just Task.from_ticket_id === this ticket.
  const linkedTasksQuery = createQuery(() => ({
    queryKey: ['project-tasks', ticket?.project_id],
    queryFn: () => tasksApi.forProject(ticket!.project_id!),
    enabled: !!ticket?.project_id
  }));
  const linkedTasks = $derived((linkedTasksQuery.data?.results ?? []).filter((t) => t.from_ticket_id === ticketId));

  const createTaskMutation = createMutation(() => ({
    mutationFn: () => ticketsApi.createTask(ticketId),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['project-tasks', ticket?.project_id] });
      toast.success(`Created ${created.key} on the board`);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function colChipKind(col: string): 'lime' | 'teal' | 'warn' | 'gray' {
    return TASK_COLUMNS.find((c) => c.id === col)?.kind ?? 'gray';
  }

  const statuses = ['open', 'triaged', 'in-progress', 'resolved', 'closed'];

  const statusMutation = createMutation(() => ({
    mutationFn: (status: string) => ticketsApi.update(ticketId, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] })
  }));
  const shipMutation = createMutation(() => ({
    mutationFn: (deploy_sha: string) => ticketsApi.update(ticketId, { deploy_sha }),
    onSuccess: (_data, deploy_sha) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      if (deploy_sha) toast.success(`Marked shipped in ${deploy_sha}`);
    }
  }));

  let commentText = $state('');
  const commentMutation = createMutation(() => ({
    mutationFn: (body: string) => ticketsApi.addComment(ticketId, body),
    onSuccess: () => {
      commentText = '';
      queryClient.invalidateQueries({ queryKey: ['ticket-comments', ticketId] });
    }
  }));
  function postComment() {
    const body = commentText.trim();
    if (!body) return;
    commentMutation.mutate(body);
  }

  function goBack() {
    if (admin) route.push({ tab: 'inbox' });
    else if (ticket?.project_id) route.push({ tab: 'project', projectId: ticket.project_id });
    else route.push({ tab: 'overview' });
  }
</script>

{#if ticketQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading ticket…</p>
{:else if ticketQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(ticketQuery.error)}</p>
{:else if ticket}
  <button type="button" class="btn btn-ghost btn-sm" style="margin-bottom:16px;margin-left:-6px" onclick={goBack}>
    <ChevronRight size={15} strokeWidth={2} />Back
  </button>
  <div class="detail-grid">
    <div>
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:14px">
        <h1 class="h-title" style="flex:1;font-size:22px">{ticket.title}</h1>
        <Chip label={pretty(ticket.status)} kind={ticketKind(ticket.status)} dot />
      </div>
      <div style="margin-bottom:18px"><SrcBadge {ticket} redact={!admin} /></div>
      {#if ticket.desc}
        <div style="font-size:14.5px;color:var(--t2);line-height:1.65;margin-bottom:26px">{ticket.desc}</div>
      {/if}

      {#if ticket.project_id}
        <div style="margin-bottom:26px">
          <div style="display:flex;align-items:center;gap:9px;margin-bottom:11px">
            <Link2 size={16} />
            <span style="font-weight:600;color:var(--t1);font-size:14px">Linked tasks</span>
            <span style="flex:1"></span>
            <button type="button" class="btn btn-secondary btn-sm" disabled={createTaskMutation.isPending} onclick={() => createTaskMutation.mutate()}>
              <Plus size={14} />Create task from ticket
            </button>
          </div>
          {#if linkedTasks.length}
            <div style="display:flex;flex-direction:column;gap:8px">
              {#each linkedTasks as t (t.id)}
                <div class="mini-task" onclick={() => route.push({ tab: 'project', projectId: t.project_id })} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && route.push({ tab: 'project', projectId: t.project_id })}>
                  <span class="task-key">{t.key}</span>
                  <span style="flex:1;font-size:13px;color:var(--t1)">{t.title}</span>
                  <Chip label={pretty(t.col)} kind={colChipKind(t.col)} />
                </div>
              {/each}
            </div>
          {:else}
            <div style="font-size:13px;color:var(--t3)">No board tasks linked yet — spin one up from this ticket.</div>
          {/if}
        </div>
      {/if}

      <div style="display:flex;align-items:center;gap:9px;margin-bottom:6px">
        <MessageSquare size={17} />
        <span style="font-weight:600;color:var(--t1);font-size:14px">Comments</span>
        <span style="font-size:12px;color:var(--t3)">({commentsQuery.data?.results.length ?? 0})</span>
      </div>
      <div>
        {#each commentsQuery.data?.results ?? [] as c (c.id)}
          <div class="comment">
            <Avatar id={c.author_user_id ?? 0} name={userName(c.author_user_id)} size={32} />
            <div class="cbody">
              <div class="chead">
                <span class="cn">{userName(c.author_user_id)}</span>
                <span class="ct">{relTime(c.created_at)}</span>
              </div>
              <div class="cx">{c.body}</div>
            </div>
          </div>
        {/each}
      </div>
      <div style="display:flex;gap:11px;margin-top:16px;align-items:flex-start">
        <Avatar id={session.me?.user.id ?? 0} name={session.me?.user.name ?? ''} size={32} />
        <div style="flex:1">
          <textarea class="textarea" style="min-height:72px" placeholder="Add a comment…" bind:value={commentText}></textarea>
          <div style="display:flex;justify-content:flex-end;margin-top:9px">
            <button type="button" class="btn btn-primary" disabled={!commentText.trim() || commentMutation.isPending} onclick={postComment}>
              Post comment
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card" style="padding:6px 16px 10px">
      <div class="meta-item">
        <div class="meta-lbl">Status</div>
        <select class="select" value={ticket.status} onchange={(e) => statusMutation.mutate((e.target as HTMLSelectElement).value)}>
          {#each statuses as s (s)}
            <option value={s}>{pretty(s)}</option>
          {/each}
        </select>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Age</div>
        <div style="display:flex;align-items:center;gap:9px;flex-wrap:wrap">
          <span style="font-size:13px;color:var(--t2)">{ticketAgeDays(ticket.created_at)} days open</span>
          <SlaChip status={ticket.status} createdAt={ticket.created_at} />
        </div>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Project</div>
        {#if ticket.project_id}
          <button type="button" class="linkchip" onclick={() => route.push({ tab: 'project', projectId: ticket.project_id! })}>
            <Folder size={15} />{projectName(ticket.project_id)}
          </button>
        {:else}
          <span style="font-size:13px;color:var(--t3)">Unassigned (in inbox)</span>
        {/if}
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Assignee</div>
        {#if ticket.assignee_user_id}
          <div style="display:flex;align-items:center;gap:9px">
            <Avatar id={ticket.assignee_user_id} name={userName(ticket.assignee_user_id)} size={26} />
            <span style="font-size:13.5px;color:var(--t1);font-weight:500">{userName(ticket.assignee_user_id)}</span>
          </div>
        {:else}
          <span style="font-size:13px;color:var(--t3)">Unassigned</span>
        {/if}
      </div>
      {#if ticket.project_id}
        <div class="meta-item">
          <div class="meta-lbl">Shipped in</div>
          <select class="select" value={ticket.deploy_sha ?? ''} onchange={(e) => shipMutation.mutate((e.target as HTMLSelectElement).value)}>
            <option value="">Not shipped yet</option>
            {#each projectDeploys as dp (dp.id)}
              <option value={dp.sha}>{dp.sha} · {dp.target}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div class="meta-item">
        <div class="meta-lbl">Source</div>
        {#if admin}
          <div style="font-size:12.5px;color:var(--t2);line-height:1.7">
            <div>Site code: <b style="color:var(--t1);font-family:var(--mono)">{siteCode(ticket.src_site, ticket.src_company, ticket.src_property)}</b></div>
            <div style="margin-top:2px"><b style="color:var(--t1)">{ticket.src_company}</b></div>
            <div>{ticket.src_property}</div>
            <div>Location: <b style="color:var(--t1)">{ticket.src_room}</b></div>
            <div style="color:var(--t3);margin-top:4px">Imported from Toyama CRM</div>
          </div>
        {:else}
          <div style="font-size:12.5px;color:var(--t2);line-height:1.7">
            <div><b style="color:var(--t1)">{siteCode(ticket.src_site, undefined, undefined)}</b></div>
            <div>Location: <b style="color:var(--t1)">{ticket.src_room}</b></div>
            <div style="color:var(--t3);margin-top:4px">Customer identity hidden · via Toyama CRM</div>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
